import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { object, string } from 'yup';
import useForgotModal from '../../hooks/useForgotModal';
import useLoginModal from '../../hooks/useLoginModal';
import useRegisterModal from '../../hooks/useRegisterModal';

import { isNil } from 'lodash-es';
import usePrelaunch from '../../hooks/usePrelaunch';
import useUser from '../../hooks/useUser';
import { loginRequest } from '../../redux/ducks/AuthDucks';
import type { LoginRequestType } from '../../redux/ducks/AuthDucks.types';
import { setLoginExpiration, setUserId } from '../../redux/ducks/StorageDucks';
import { setLoginModalOpen } from '../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../redux/reducers/RootReducer.types';
import Input from '../inputs/Input';
import Heading from '../layout/Heading';
import Modal from './Modal';

type LoginModalUserInput = {
  email: string;
  password: string;
};

const forceNonBeta = true;

const LoginModal = () => {
  const { currentUser } = useUser();
  const { isPrelaunch } = usePrelaunch();
  const registerModal = useRegisterModal();
  const loginModal = useLoginModal();
  const forgotModal = useForgotModal();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { loginResponse } = useSelector((state: RootReducerType) => state.auth);
  const clientId = useSelector(
    (state: RootReducerType) => state.storage.clientId,
  );
  const { t } = useTranslation();

  const formSchema = object().shape({
    email: string()
      .email(t('static.login.validation_error_email_invalid'))
      .required(t('static.login.validation_error_email_required')),
    password: string()
      .required(t('static.login.validation_error_password_required'))
      .min(6, t('static.login.validation_error_password_short'))
      .max(40, t('static.login.validation_error_password_long')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginModalUserInput>({
    resolver: yupResolver(formSchema),
  });

  const onSubmit: SubmitHandler<LoginModalUserInput> = (data) => {
    setIsLoading(true);

    // dispatch on submit action
    const payload: LoginRequestType = {
      email: data.email,
      password: data.password,
      client: clientId || '',
    };

    dispatch(loginRequest(payload));
  };

  useEffect(() => {
    if (loginResponse && isLoading) {
      if (loginResponse?.data) {
        // update user id and refresh token
        dispatch(setUserId(loginResponse.data.uuid));
        dispatch(setLoginExpiration(loginResponse.data.expiration));
        // dispatch(refreshRequest(true)); // user provider will take care of it
      } else {
        setIsLoading(false);
        toast.error(t('static.login.notification_cannot_login'));
      }
    }
  }, [loginResponse]);

  useEffect(() => {
    if (!isNil(currentUser) && isLoading) {
      setIsLoading(false);
      loginModal.onClose();

      /*
      if (refreshResponse?.data) {
        // update user id and refresh token
        dispatch(
          setAccessToken({
            token: refreshResponse.data.token,
            expires: refreshResponse.data.expiration,
          }),
        );
        // dispatch(userGetProfile(true)); // user provider will take care of it

        loginModal.onClose();
      } else {
        toast.error(t('static.login.notification_cannot_login'));
      }
        */
    }
  }, [currentUser]);

  const toggleRegister = useCallback(() => {
    loginModal.onClose();
    registerModal.onOpen();
  }, [loginModal, registerModal]);

  const toggleForgot = useCallback(() => {
    loginModal.onClose();
    forgotModal.onOpen();
  }, [loginModal, registerModal]);

  const bodyContent = (
    <div className="flex flex-col gap-4">
      <Heading
        title={t('static.login.modal_header_title')}
        subtitle={t('static.login.modal_header_subtitle')}
      />
      <Input
        id="email"
        label={t('static.login.modal_input_email_label')}
        disabled={isLoading}
        register={register}
        errors={errors}
        type="email"
        autoComplete="email"
      />
      <Input
        id="password"
        type="password"
        label={t('static.login.modal_input_password_label')}
        disabled={isLoading}
        register={register}
        errors={errors}
        autoComplete="current-password"
      />
      <button
        data-test-id="login-toggle-forgot-btn"
        id="login-toggle-forgot-btn"
        key="login-toggle-forgot-btn"
        aria-label="login-toggle-forgot-btn"
        type="button"
        onClick={toggleForgot}
        className="cursor-pointer text-right text-sm font-light text-neutral-500 hover:underline"
      >
        {t('static.login.modal_forgot_text')}
      </button>
    </div>
  );

  const footerContent = (
    <div className="mt-3 flex flex-col gap-4">
      {(!isPrelaunch || forceNonBeta) && (
        <>
          <hr />
          <div className="mt-4 text-center font-light text-neutral-500">
            <div className="flex flex-row items-center justify-center gap-2">
              <div>
                {t('static.login.modal_register_text', {
                  name: t('dynamic.common_company_name'),
                })}
              </div>
              <button
                data-test-id="login-modal-register-switch-btn"
                id="login-modal-register-switch-btn"
                key="login-modal-register-switch-btn"
                aria-label="login-modal-register-switch-btn"
                className="cursor-pointer text-neutral-800 hover:underline"
                type="button"
                onClick={toggleRegister}
              >
                {t('static.login.modal_register_label')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Modal
      id="LoginModal"
      setIsOpen={(open) => dispatch(setLoginModalOpen(open))}
      disabled={isLoading}
      loading={isLoading}
      isOpen={loginModal.isOpen}
      title={t('static.login.modal_title')}
      actionLabel={t('static.login.modal_submit_btn_label')}
      onClose={loginModal.onClose}
      onSubmit={handleSubmit(onSubmit)}
      body={bodyContent}
      footer={footerContent}
      noFit
    />
  );
};

export default LoginModal;
